import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import style from "./styles.module.scss";

const keyI18n = "translations.admin.account";
const MENU_ID = "admin-account-menu";

interface Props {
  name: string;
  disabled?: boolean;
  onChangePassword(): void;
  onLogout(): void;
}

export default function AccountMenu({ name, disabled, onChangePassword, onLogout }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const firstItem = menuRef.current && menuRef.current.querySelector<HTMLButtonElement>("[role=menuitem]");
    if (firstItem) {
      firstItem.focus();
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        if (toggleRef.current) {
          toggleRef.current.focus();
        }
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const moveFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }
    event.preventDefault();
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>("[role=menuitem]"));
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === "ArrowDown" ? index + 1 : index - 1;
    items[(next + items.length) % items.length].focus();
  };

  const select = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <div className={style.accountMenu} ref={containerRef}>
      <button
        type="button"
        ref={toggleRef}
        className={style.accountToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={MENU_ID}
        aria-label={t(`${keyI18n}.open`, { name })}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true" className="material-symbols-sharp">account_circle</span>
        <span className={`pgm-label-large ${style.userName}`}>{name}</span>
        <span aria-hidden="true" className={`material-symbols-sharp ${style.accountChevron}`}>
          {open ? "arrow_drop_up" : "arrow_drop_down"}
        </span>
      </button>

      {open && (
        <div id={MENU_ID} role="menu" ref={menuRef} className={style.menu} onKeyDown={moveFocus}>
          <button type="button" role="menuitem" className={`pgm-label-large ${style.menuItem}`} onClick={() => select(onChangePassword)}>
            <span aria-hidden="true" className="material-symbols-sharp">lock_reset</span>
            {t(`${keyI18n}.changePassword`)}
          </button>
          <button type="button" role="menuitem" className={`pgm-label-large ${style.menuItem}`} onClick={() => select(onLogout)}>
            <span aria-hidden="true" className="material-symbols-sharp">logout</span>
            {t(`${keyI18n}.logout`)}
          </button>
        </div>
      )}
    </div>
  );
}
